import { Check, Globe } from "lucide-react";
import { useI18n } from "../i18n/I18nProvider";
import { useDismissable } from "../hooks/useDismissable";

/**
 * Compact luxury language switcher.
 * Shows the short code (IT/EN/…) with a globe icon. Opens a dropdown with
 * the five available languages.
 */
export default function LanguageSwitcher({ variant = "header" }) {
  const { lang, setLang, langs } = useI18n();
  const { open, setOpen, ref } = useDismissable();

  const current = langs.find((l) => l.code === lang) || langs[0];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="language-switcher-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`inline-flex items-center gap-2 h-10 sm:h-11 px-3 sm:px-4 rounded-full border transition-all ${
          variant === "header"
            ? "border-ink-200 bg-white/70 hover:border-ink-900 hover:bg-white"
            : "border-ink-200 bg-white hover:border-ink-900"
        }`}
      >
        <Globe className="w-3.5 h-3.5" strokeWidth={1.6} />
        <span className="font-mono text-[11px] tracking-[0.22em] text-ink-900">
          {current.short}
        </span>
      </button>

      {open && (
        <div
          role="listbox"
          data-testid="language-switcher-menu"
          className="absolute right-0 mt-2 w-52 rounded-md border border-ink-200 bg-white shadow-[0_20px_60px_-25px_rgba(18,18,18,0.25)] overflow-hidden z-50"
        >
          <ul className="py-1">
            {langs.map((l) => (
              <li key={l.code}>
                <button
                  type="button"
                  onClick={() => {
                    setLang(l.code);
                    setOpen(false);
                  }}
                  data-testid={`lang-option-${l.code}`}
                  className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-[13px] transition-colors ${
                    l.code === lang
                      ? "bg-ink-100 text-ink-900"
                      : "text-ink-700 hover:bg-ink-50 hover:text-ink-900"
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <span className="font-mono text-[10px] tracking-[0.2em] text-ink-500 w-6">
                      {l.short}
                    </span>
                    <span className="font-display">{l.label}</span>
                  </span>
                  {l.code === lang && (
                    <Check className="w-4 h-4 text-ink-900" strokeWidth={1.6} />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
